// **The sandbox screen's portrait geometry, as arithmetic rather than as a flex hope** (task 6.0,
// artifacts review **B5**; design §3, visual spec §1.2).
//
// The screen is one column: a world view with a control stack of exactly three bars pinned under
// it. The spec's requirement is not "the bars are at the bottom" — it is that the world view takes
// **all** the height the stack leaves, with no gap and no overflow, and that focusing the prompt
// makes the world **give up** exactly the height the stack takes rather than letting the expanded
// field float over the bees the player is describing.
//
// Two reasons that lives here instead of inside `SandboxScreen.tsx`:
//
//   * **jsdom measures nothing.** Every `getBoundingClientRect` is zero under the test environment
//     this change is proven in, so a layout expressed as `flex: 1` is a layout no test can hold to
//     account. Expressed as numbers it is a table-driven assertion, and the screen applies the
//     numbers it is given — the same "the logic is not in the component" rule the coordinator
//     follows (design **T1**).
//   * **The three bars are built by three different components**, none of which declares a height
//     budget (they are all content-sized). Somebody has to own the budget; a module both the screen
//     and its tests read is the only place it cannot silently drift.
//
// The numbers are measured off the promoted golden reference
// (`prototype/ui_kits/waggle-app/sandbox.html`; `scratchpad/visual-acceptance.md`). Note the
// deliberate departure recorded in visual spec §0.4 quirk 1: the kit's `.stack` carries a 1.5 px
// top border inside its 212 px border-box, so flexbox shaves the bee-type bar to 62.5 px in the
// browser. The nominal design intent is 88 / 64 / 60, and the spec's own instruction is to fix the
// discrepancy by giving the stack the extra 1.5 px rather than by shrinking a bar — so this module
// states the intent, and the screen draws the stack's rule as an inset line rather than as a border
// that eats content height.

/** The reference frame's width (visual spec §1.1). Advisory: the screen fills its host's width. */
export const REFERENCE_FRAME_WIDTH = 390;

/**
 * The reference frame's height. The screen takes a real frame height from its host and falls back
 * to this — a fallback, never an assumption, so a taller phone gives its extra pixels to the world
 * view rather than to a gap.
 */
export const REFERENCE_FRAME_HEIGHT = 844;

/** ① The playback bar (visual spec §3): a 44 px transport row over a 24 px scrub row. */
export const PLAYBACK_BAR_HEIGHT = 88;

/** ② The bee-type bar (visual spec §4): 48 px chips with 8 px padding. */
export const BEE_TYPE_BAR_HEIGHT = 64;

/** ③ The Beekeeper input at rest (visual spec §5.1): one 44 px field row. */
export const PROMPT_BAR_HEIGHT = 60;

/**
 * ③ The Beekeeper input focused (visual spec §5.2): a 70 px field showing **three** wrapped lines.
 *
 * Three is a computed stop, not a round number: nominally the field's content box is 70 px less
 * 14 px of padding and 3 px of border (1.5 px a side) ≈ 53 px, and a 13 px line at 1.45 line-height
 * is 18.85 px, so the box admits a little under three lines. Anything past the third scrolls inside
 * the field. One line is enough to type a sentence and not enough to re-read one, and re-reading is
 * the point — a prompt to the Beekeeper is a diagnosis.
 *
 * **Do not treat that as an exact invariant.** A 1.5 px border is subject to device-pixel snapping,
 * so the realised content box moves with the display: 54.4 px measured at devicePixelRatio 1.25,
 * 54 px at 1. The figure is a design intent, not a constant to assert against — the browser tests in
 * `SandboxScreen.browser.spec.ts` derive it from computed styles at runtime for exactly this reason.
 *
 * (This paragraph read "70 − 14 = 56 px … ≈ 2.97 lines" until `sandbox-prompt-field-padding`
 * measured it. It omitted the border, and also the field's own 2 px user-agent padding — which
 * mattered once that change clamped the field to this box, because the padding then came out of the
 * draft's room rather than sitting outside it. `SandboxScreen.tsx`'s text area now zeroes it on the
 * block axis while focused, which is what keeps the realised capacity near three lines. `rows={3}`
 * is unchanged: only the arithmetic explaining it was wrong.)
 */
export const PROMPT_BAR_HEIGHT_FOCUSED = 86;

/**
 * How the stack trades height with the world view when the prompt grows on focus and settles on
 * blur.
 *
 * The growth itself is specified — the input grows on focus and the world gives up exactly the
 * height the stack takes — but nothing said how it *arrives*, so it arrived instantly and read as a
 * jolt under the player's thumb. This is the interpolation, not a change to either height.
 *
 * **Tokens, not numbers.** UI.md §2 fixes the motion scale at 120/240 ms, and the kit already
 * publishes both (`--dur-fast`, `--dur-med`) alongside its easings. A hand-picked duration here
 * would be a scattered constant *and* off the sanctioned scale.
 *
 * `--ease-out` rather than `--ease-pop`: the pop easing overshoots, and UI.md scopes it to taps and
 * sheet arrivals. This is neither — it is two panels trading height across a shared seam, and an
 * overshoot would send them past each other in opposite directions and visibly split the seam before
 * settling.
 *
 * One constant, because the world view, the stack and the prompt's own frame all have to move on the
 * same clock: the world is anchored to the top edge and the stack to the bottom, so any difference in
 * timing opens a gap between them mid-transition.
 */
export const PROMPT_GROWTH_TRANSITION = 'height var(--dur-fast) var(--ease-out)';

/**
 * ③ The Beekeeper input's **re-entry row**, when a closed thread exists to return to
 * (`client-beekeeper-chat`): a 44 px tappable row plus the 6 px gap above the field. Added to the
 * prompt bar's budget rather than floated over the world, so the world view still gives up exactly
 * the height the stack takes — the same rule the focused field already obeys.
 */
export const PROMPT_BAR_THREAD_ROW_HEIGHT = 50;

/**
 * The gap between the control stack's top edge and the camera rail that rides it (visual spec
 * §1.5): `bottom = stack height + 14`, in both states, so the rail moves with the stack instead of
 * being covered by it.
 */
export const CAMERA_RAIL_GAP = 14;

/** Every height the screen draws, derived together so none can be set without the others. */
export interface SandboxLayout {
  /** The frame the screen was given. */
  readonly frameHeight: number;
  /** What is left for the world view once the stack has taken its share. */
  readonly worldHeight: number;
  /** The three bars' total. */
  readonly stackHeight: number;
  readonly playbackHeight: number;
  readonly typesHeight: number;
  readonly promptHeight: number;
  /** The camera rail's `bottom` offset — the stack's top edge plus {@link CAMERA_RAIL_GAP}. */
  readonly cameraRailBottom: number;
}

export interface SandboxLayoutInput {
  /** The host's measured height. Defaults to the reference frame. */
  readonly frameHeight?: number;
  /** The Beekeeper input holds focus, so the third bar is showing three lines of the draft. */
  readonly promptFocused?: boolean;
  /** A closed Beekeeper thread exists, so the third bar is also showing its re-entry row. */
  readonly threadRowVisible?: boolean;
}

/**
 * The screen's heights for one state.
 *
 * `worldHeight` is a **remainder**, never an independent number: that is what makes "no gap and no
 * overflow" true by construction instead of by two constants happening to agree. It is floored at
 * zero so a frame shorter than the control stack degrades to a stack-only screen rather than to a
 * negative height, which browsers clamp and tests would then read as an accidental pass.
 */
export function sandboxLayout({
  frameHeight = REFERENCE_FRAME_HEIGHT,
  promptFocused = false,
  threadRowVisible = false,
}: SandboxLayoutInput = {}): SandboxLayout {
  const promptHeight =
    (promptFocused ? PROMPT_BAR_HEIGHT_FOCUSED : PROMPT_BAR_HEIGHT) +
    (threadRowVisible ? PROMPT_BAR_THREAD_ROW_HEIGHT : 0);
  const stackHeight = PLAYBACK_BAR_HEIGHT + BEE_TYPE_BAR_HEIGHT + promptHeight;
  return {
    frameHeight,
    worldHeight: Math.max(0, frameHeight - stackHeight),
    stackHeight,
    playbackHeight: PLAYBACK_BAR_HEIGHT,
    typesHeight: BEE_TYPE_BAR_HEIGHT,
    promptHeight,
    cameraRailBottom: stackHeight + CAMERA_RAIL_GAP,
  };
}

// --- The version selector's panel, pinned to the stack's *real* top edge (WAG-91) --------------
//
// The panel is `position: fixed` and full-width rather than anchored to its trigger, because the
// trigger is a narrow flexing cell inside the playback bar and would truncate every summary (visual
// spec §8.1). What it is fixed *to* was the defect: the composition root wrote its `bottom` as
// `PLAYBACK_BAR_HEIGHT + BEE_TYPE_BAR_HEIGHT + PROMPT_BAR_HEIGHT` — 212, the stack's **resting**
// height — while {@link sandboxLayout} grows the stack to 262 whenever the Beekeeper's re-entry row
// is showing ({@link PROMPT_BAR_THREAD_ROW_HEIGHT}) and to 238 with the prompt focused. A panel
// pinned 50px too low covers the playback bar, which is where its own trigger lives: the player
// could open the selector and then had nothing left to press to close it.
//
// The fix is not a fourth constant here — it is that the panel reads the height the screen is
// **actually drawing**, published by the screen as a custom property. Two consequences follow, and
// both are in the issue: the panel moves with the stack in every state, and it grows *upward* into
// the world view, so a taller screen gives the list more room instead of the same fixed slab.
//
// A custom property rather than a prop, because the selector reaches the screen as an opaque
// `ReactNode` slot (`SandboxScreenProps.versionSelector`): the screen does not compose the panel and
// the root does not measure the screen, so inheritance through the DOM is the one seam that already
// joins them. `fixed` does not break custom-property inheritance — the panel is still a descendant
// of the frame that publishes them.

/** The published stack height — the panel's own `bottom`. */
export const SANDBOX_STACK_HEIGHT_VAR = '--sandbox-stack-height';

/** The published world-view height — what bounds how far the panel may grow upward. */
export const SANDBOX_WORLD_HEIGHT_VAR = '--sandbox-world-height';

/**
 * How much of the world view the panel leaves uncovered: 12px at the top matching the panel's own
 * 12px side gutters, doubled so the arithmetic is symmetric about the world view rather than
 * measured from one edge.
 */
export const VERSION_MENU_WORLD_MARGIN = 24;

/**
 * The custom properties a sandbox frame publishes for anything positioned against its geometry.
 *
 * Derived from a {@link SandboxLayout} rather than from the input, so the published numbers are the
 * ones the screen draws with — there is no second computation to drift.
 */
export function sandboxLayoutVars(layout: SandboxLayout): Record<string, string> {
  return {
    [SANDBOX_STACK_HEIGHT_VAR]: `${layout.stackHeight}px`,
    [SANDBOX_WORLD_HEIGHT_VAR]: `${layout.worldHeight}px`,
  };
}

/**
 * Where the version selector's panel sits, as one style object.
 *
 * Static — it names the published properties instead of resolving them, so the panel needs no
 * measurement, no re-render on a stack change, and no knowledge of which state the prompt is in
 * (the "geometry-independence" rule the sandbox popups follow). The fallbacks are the resting stack
 * and a conservative fraction of the viewport, for a host that publishes neither.
 */
export function versionSelectorListStyle(): {
  position: 'fixed';
  left: number;
  right: number;
  bottom: string;
  maxHeight: string;
  overflowY: 'auto';
} {
  return {
    position: 'fixed',
    left: 12,
    right: 12,
    bottom: `var(${SANDBOX_STACK_HEIGHT_VAR}, ${RESTING_STACK_HEIGHT}px)`,
    maxHeight: `calc(var(${SANDBOX_WORLD_HEIGHT_VAR}, 60vh) - ${VERSION_MENU_WORLD_MARGIN}px)`,
    overflowY: 'auto',
  };
}

/** The stack at rest — the fallback, and the number the panel used to be pinned to unconditionally. */
const RESTING_STACK_HEIGHT = PLAYBACK_BAR_HEIGHT + BEE_TYPE_BAR_HEIGHT + PROMPT_BAR_HEIGHT;

// --- The phase clock's corner slot, the submitted-set banner's slot, and the counter stack's
// offset (`rounds-client` task 5.1/5.2, design **D9**) ------------------------------------------
//
// `client-sandbox-screen`'s composition requirement fixes the G5 phase clock's **resting**
// mounting as "the world view's top-leading corner, opposite the counter stack ... sized to match
// the counter pill". That is a second absolutely-positioned HUD element sharing this screen's top
// strip with the counters, and D9 is explicit that its slot — and the submitted-set banner
// (`client-rounds-selection` task 7.9) that later competes with it for the same strip — belong
// here rather than as a second, drifting source of geometry inside the screen or the banner's own
// component.
//
// The numbers are measured off `prototype/sketches/rounds-client/phase-clock-slot.html` (the
// clock's own corner, decided **A + D**) and `selection-affordance.html`'s panel 3 (the banner and
// the counter stack's resulting positions) — the same "measured off the promoted golden reference"
// discipline this module's header describes for the three-bar stack.
//
// **`prototypes.md`'s "the counter stack moves down ~48px" understates the shift, and the sketch
// is the authority.** Measured against `_frame.css`'s `.counters { top: 14px }` and panel 3's own
// `style="top:106px"` override, the counter stack moves **92px**, not 48. 48 is the banner's own
// footprint ({@link SUBMITTED_BANNER_HEIGHT_PX} 40 + {@link SUBMITTED_BANNER_GAP_BELOW_PX} 8) —
// its height, not the distance anything travels. The two are easy to conflate and the prose did.
//
// Note also that the **corner clock contributes nothing** to this shift: it mirrors the counters
// across the screen (`left:12` against their `right:12`) on the same strip, so it never overlaps
// them. The whole 92px is the banner's, which is why {@link COUNTER_STACK_TOP_PX} is unconditional
// and only the banner has a second, larger variant.
//
// Recorded here rather than silently implemented, because two later tasks read this number: the
// `prototypes.md` ledger row is corrected when that ledger is resolved, and `docs/specs/UI.md` §4
// is where "the measured argument" is written down — and "~48px" must not be what lands there.

/** The corner pill's top offset. Identical to the counter stack's own (`client-sandbox-screen`:
 * "opposite the counters"), so the two pills sit on one shared strip rather than two. */
export const PHASE_CLOCK_TOP_PX = 14;

/** The corner pill's leading offset. The counter stack sits at `right: 12`; the clock mirrors it
 * at `left: 12` — which is what "the corner opposite the counters" means in pixels. */
export const PHASE_CLOCK_LEFT_PX = 12;

/**
 * The corner pill's own height — "sized to match the counter pill exactly" (`client-sandbox-screen`).
 * Restated as its own constant rather than imported from `ResourceCounter.tsx`'s
 * `COUNTER_PILL_HEIGHT`, so this pure layout module stays free of a component-layer dependency; the
 * two are pinned together by this comment and by `PhaseClock.test.tsx`'s own assertion that its
 * corner variant renders at this height.
 */
export const PHASE_CLOCK_HEIGHT_PX = 34;

/** The gap between the clock's bottom edge and the submitted-set banner beneath it
 * (`selection-affordance.html` panel 3: the banner's `top:58` against a clock bottom edge of
 * `14 + 34 = 48`). */
export const SUBMITTED_BANNER_GAP_ABOVE_PX = 10;

/**
 * The submitted-set banner's own top, once the corner clock has taken the strip above it. A
 * **derived** number, never an independent one — moving the clock's height or this gap moves the
 * banner with it, the same "no gap and no overflow" discipline {@link sandboxLayout} already
 * applies to the three-bar stack.
 */
export const SUBMITTED_BANNER_TOP_PX =
  PHASE_CLOCK_TOP_PX + PHASE_CLOCK_HEIGHT_PX + SUBMITTED_BANNER_GAP_ABOVE_PX;

/** The submitted-set banner's own height (`selection-affordance.html`'s `.subbanner`). */
export const SUBMITTED_BANNER_HEIGHT_PX = 40;

/** The gap between the banner's bottom edge and the counter stack beneath it. */
export const SUBMITTED_BANNER_GAP_BELOW_PX = 8;

/** The counter stack's ordinary top offset — unaffected by the corner clock, which sits on the
 * opposite side and never overlaps it. Equal to {@link PHASE_CLOCK_TOP_PX} by construction: the
 * two pills mirror each other on the same strip. */
export const COUNTER_STACK_TOP_PX = PHASE_CLOCK_TOP_PX;

/**
 * The counter stack's top once the submitted-set banner is showing: `106`, matching
 * `selection-affordance.html` panel 3's own `style="top:106px"`.
 *
 * Derived from the banner's slot rather than written down, so the chain stays honest — but note
 * it is a **position**, not a delta. The distance travelled is
 * `106 − {@link COUNTER_STACK_TOP_PX}` = 92px; see this module's header for why the ledger's
 * "~48px" is not that number.
 */
export const COUNTER_STACK_TOP_WITH_SUBMITTED_BANNER_PX =
  SUBMITTED_BANNER_TOP_PX + SUBMITTED_BANNER_HEIGHT_PX + SUBMITTED_BANNER_GAP_BELOW_PX;

/** The corner strip's arithmetic for one render: the clock's own slot, the banner's slot beneath
 * it, and where that leaves the counter stack. */
export interface SandboxHudLayout {
  readonly phaseClockTop: number;
  readonly phaseClockLeft: number;
  readonly phaseClockHeight: number;
  readonly submittedBannerTop: number;
  readonly submittedBannerHeight: number;
  /** Where the counter stack's `top` should sit, given whether the banner is showing. */
  readonly counterStackTop: number;
}

export interface SandboxHudLayoutInput {
  /** The submitted-set banner (`client-rounds-selection`) is visible, so the counter stack gives
   * up the room the corner strip needs. `false` — the ordinary case — leaves the counters at
   * their ordinary top; the corner clock never competes with them for it, because it sits on the
   * opposite side. */
  readonly submittedBannerVisible?: boolean;
}

/**
 * A second function rather than folding into {@link sandboxLayout}: these numbers are independent
 * of the frame height and the prompt's focus state, so mixing the two would make every caller of
 * one recompute the other for nothing.
 */
export function sandboxHudLayout({
  submittedBannerVisible = false,
}: SandboxHudLayoutInput = {}): SandboxHudLayout {
  return {
    phaseClockTop: PHASE_CLOCK_TOP_PX,
    phaseClockLeft: PHASE_CLOCK_LEFT_PX,
    phaseClockHeight: PHASE_CLOCK_HEIGHT_PX,
    submittedBannerTop: SUBMITTED_BANNER_TOP_PX,
    submittedBannerHeight: SUBMITTED_BANNER_HEIGHT_PX,
    counterStackTop: submittedBannerVisible
      ? COUNTER_STACK_TOP_WITH_SUBMITTED_BANNER_PX
      : COUNTER_STACK_TOP_PX,
  };
}
